import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { useMessage } from './MessageContext';
import { useNotifications } from './NotificationContext';
import { useWalletAuth } from '../hooks/useWalletAuth';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const { address, isConnected, isConnecting, isReconnecting } = useAccount();
  const { error: connectError } = useConnect();
  const { success, error, warning, info } = useMessage();
  const { addNotification } = useNotifications();
  
  // 使用钱包认证hook
  const {
    isAuthenticated,
    isAuthenticating,
    authError,
    user: authUser,
    signInWithWallet,
    signOut,
    needsSignIn,
    isLoggedIn,
    clearError
  } = useWalletAuth();
  
  const prevConnectedRef = useRef(isConnected);
  const prevConnectingRef = useRef(isConnecting);
  const prevReconnectingRef = useRef(isReconnecting);
  const isInitializedRef = useRef(false);
  
  // 用户信息：优先使用后端返回的用户信息，否则使用钱包地址
  const user = authUser || (isConnected ? {
    id: address,
    address: address,
    username: `${address?.slice(0, 6)}...${address?.slice(-4)}`,
  } : null);

  // 监听钱包连接状态变化
  useEffect(() => {
    // 跳过初始化时的状态检查，避免页面刷新时重复显示消息
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      prevConnectedRef.current = isConnected;
      return;
    }

    // 连接成功（从未连接状态变为连接状态）
    if (isConnected && !prevConnectedRef.current && address) {
      // success('Wallet connected successfully!');
      // 添加到通知列表
      addNotification({
        title: 'Wallet Connected',
        message: `Successfully connected to ${address.slice(0, 8)}...${address.slice(-6)}`,
        type: 'success',
        category: 'system'
      });
      // console.log('✅ Wallet connected:', address);
    }
    // 断开连接（从连接状态变为未连接状态）
    else if (!isConnected && prevConnectedRef.current && !isConnecting) {
      // 使用 setTimeout 避免在渲染过程中调用 setState
      setTimeout(() => {
        info('Wallet disconnected');
      }, 0);
      // console.log('❌ Wallet disconnected');
    }
    
    prevConnectedRef.current = isConnected;
  }, [isConnected, address, isConnecting, info]);

  // 监听连接状态变化
  useEffect(() => {
    // 跳过初始化时的状态检查
    if (!isInitializedRef.current) {
      prevConnectingRef.current = isConnecting;
      prevReconnectingRef.current = isReconnecting;
      return;
    }

    // 开始连接（从未连接状态变为连接中）- 只在用户主动操作时显示
    if (isConnecting && !prevConnectingRef.current && !isReconnecting) {
      setTimeout(() => {
        info('Connecting to wallet...');
      }, 0);
      // console.log('🔄 Connecting to wallet...');
    }
    // 重新连接消息已被禁用，因为页面刷新时会误触发
    // else if (isReconnecting && !prevReconnectingRef.current) {
    //   setTimeout(() => {
    //     info('Reconnecting to wallet...');
    //   }, 0);
    //   // console.log('🔄 Reconnecting to wallet...');
    // }
    
    prevConnectingRef.current = isConnecting;
    prevReconnectingRef.current = isReconnecting;
  }, [isConnecting, isReconnecting, info]);

  // 监听连接错误
  useEffect(() => {
    if (connectError) {
      setTimeout(() => {
        if (connectError.message.includes('User rejected')) {
          warning('Connection cancelled by user');
        } else if (connectError.message.includes('network') || connectError.message.includes('Network')) {
          error('Network connection failed. Please check your internet.');
        } else {
          error('Failed to connect wallet. Please try again.');
        }
      }, 0);
      // console.log('❌ Connection error:', connectError.message);
    }
  }, [connectError, error, warning]);

  // console.log('🔄 AuthProvider 状态:', { isLoggedIn, address, isConnected });

  // 监听认证错误
  useEffect(() => {
    if (authError) {
      setTimeout(() => {
        error(`Authentication failed: ${authError}`);
      }, 0);
      // console.log('❌ 认证错误:', authError);
    }
  }, [authError, error]);

  const value = {
    // 状态
    isLoggedIn,
    isAuthenticated,
    isAuthenticating,
    user,
    address,
    isConnected,
    needsSignIn,
    authError,
    
    // 方法
    signInWithWallet,
    signOut,
    clearError,
    
    // 兼容性
    login: signInWithWallet,
    logout: signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
